import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, Shield, Trophy, Users, Sparkles, Compass, ChevronDown } from "lucide-react";

/**
 * Public browse page for the eight canonical Playden Houses. Serves as the
 * hub for Update 1 (identity + membership) and Update 2 (rankings + trophies).
 * A dedicated /houses/:key detail page can layer on top later; for the thin
 * slice we let each card link out to `/houses#{key}` and expand inline.
 */
export default function HousesPage() {
  const [openKey, setOpenKey] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["houses"],
    queryFn: async () => (await api.get("/houses")).data,
    staleTime: 30_000,
  });
  const houses = data?.houses || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="houses-page">
      <div className="flex items-center gap-2 mb-2">
        <Crown className="h-4 w-4 text-amber-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300">Playden Houses</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.05em] text-white leading-[1.05]">
        PLEDGE. COMPETE. WIN THE BANNER.
      </h1>
      <p className="mt-2 text-white/60 text-sm sm:text-base max-w-2xl">
        Cards pledge to a House — not players. Each card only earns its House&apos;s achievements
        while it stays pledged. Every unpledged card competes under <span className="font-mono text-slate-400">Unhoused</span>.
      </p>

      {isLoading ? (
        <div className="mt-8 text-white/50 text-sm" data-testid="houses-loading">Loading…</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="houses-grid">
          {houses.map((h) => (
            <HouseCard key={h.key} h={h} open={openKey === h.key} onToggle={() => setOpenKey(openKey === h.key ? null : h.key)} />
          ))}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-4 text-[13px] text-white/60">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300 mb-1.5">
          <Sparkles className="h-3 w-3" /> How pledging works
        </div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Open any card page and hit <strong>Pledge House</strong>. The card carries its House with it into every match.</li>
          <li>Change House whenever you want — but its current House contributions and Trophy Room spots are wiped.</li>
          <li>Guilds can pledge to one House too. Cards and Guilds are independent — a card doesn&apos;t inherit its Guild&apos;s House.</li>
        </ul>
      </div>
    </div>
  );
}

function HouseCard({ h, open, onToggle }) {
  const s = h.stats || {};
  const tint = h.color || "#94a3b8";
  const top3 = useMemo(() => {
    const rows = [];
    for (const cat of Object.keys(s.by_category || {})) {
      if ((s.by_category[cat] || 0) > 0) rows.push({ cat, pts: s.by_category[cat] });
    }
    rows.sort((a, b) => b.pts - a.pts);
    return rows.slice(0, 3);
  }, [s.by_category]);
  return (
    <button
      type="button"
      id={h.key}
      data-testid={`house-card-${h.key}`}
      onClick={onToggle}
      className="text-left rounded-2xl border p-4 transition-colors"
      style={{
        borderColor: `${tint}55`,
        background: open ? `${tint}18` : `${tint}0a`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center text-xl shrink-0"
          style={{ background: `${tint}22`, border: `1px solid ${tint}44` }}
        >
          <span>{h.crest || "🏛"}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display text-lg text-white truncate">{h.name}</div>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: tint }}>
              #{h.rank}
            </span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 truncate">{h.motto}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
        <Stat label="Cards" value={s.card_count?.toLocaleString?.() ?? 0} icon={Shield} tint={tint} testid={`house-${h.key}-cards`} />
        <Stat label="Guilds" value={s.guild_count?.toLocaleString?.() ?? 0} icon={Users} tint={tint} testid={`house-${h.key}-guilds`} />
        <Stat label="Points" value={s.total_points?.toLocaleString?.() ?? 0} icon={Trophy} tint={tint} testid={`house-${h.key}-points`} />
      </div>
      {h.deep_dive && (h.deep_dive.dives_total > 0 || h.deep_dive.deepest_dive > 0) && (
        <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-[11px]" data-testid={`house-${h.key}-deep-dive`}>
          <Stat label="Dives" value={(h.deep_dive.dives_total ?? 0).toLocaleString?.()} icon={Compass} tint={tint} testid={`house-${h.key}-dives`} />
          <Stat label="Deepest" value={h.deep_dive.deepest_dive ?? 0} icon={ChevronDown} tint={tint} testid={`house-${h.key}-deepest`} />
        </div>
      )}

      {open && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          <div className="text-[12.5px] text-white/70 leading-relaxed">{h.lore}</div>
          {top3.length > 0 ? (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/45 mb-1">Top categories</div>
              <div className="flex flex-wrap gap-1.5">
                {top3.map((r) => (
                  <span key={r.cat} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px]" style={{ color: tint }}>
                    {r.cat} · {r.pts.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">No contributions yet · pledge a card to open the leaderboard</div>
          )}
          <div className="pt-1">
            <Link
              to={`/houses/${h.key}`}
              data-testid={`house-${h.key}-detail-link`}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] hover:underline mr-3"
              style={{ color: tint }}
            >
              Open Trophy Room →
            </Link>
            <Link
              to="/collection"
              data-testid={`house-${h.key}-pledge-cta`}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] hover:underline"
              style={{ color: tint }}
            >
              Pledge a card
            </Link>
          </div>
        </div>
      )}
    </button>
  );
}

function Stat({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-white/10 bg-black/25 px-2 py-1.5" data-testid={testid}>
      <Icon className="h-3.5 w-3.5" style={{ color: tint }} />
      <div className="min-w-0">
        <div className="text-white truncate">{value}</div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 leading-none">{label}</div>
      </div>
    </div>
  );
}
